'use client';

import type { ReactNode } from 'react';
import { useState, useMemo, useEffect } from 'react';
import Cookies from 'js-cookie';
import { CustomerKeyType, WISHLIST_GUEST_USER_COOKIE_KEY } from '../constants';
import { createSafeContext } from './createSafeContext';
import { getWishListCustomerKeyCookie } from '../utils';
import { useCallbackRef } from '@skin-seoul/utils';

interface WishlistCustomerKeyContextValue {
  customerKey: string | undefined;
  customerKeyType: CustomerKeyType;
  setCustomerKey: (customerKey: string) => void;
}

const [WishlistCustomerKeyContextProvider, useWishlistCustomerKeyContext] =
  createSafeContext<WishlistCustomerKeyContextValue>('WishlistCustomerKey');

interface WishlistCustomerKeyProviderProps {
  children: ReactNode;
  getWishlistCustomerKey?: () =>
    | { wishlistGuestKey: string | undefined }
    | Promise<{ wishlistGuestKey: string | undefined }>;
  setWishlistCustomerKey?: (customerKey: string) => void | Promise<void>;
}

export const WishlistCustomerKeyProvider = ({
  children,
  getWishlistCustomerKey: _getWishlistCustomerKey,
  setWishlistCustomerKey: _setWishlistCustomerKey,
}: WishlistCustomerKeyProviderProps) => {
  const getWishlistCustomerKey = useCallbackRef(_getWishlistCustomerKey ?? getWishListCustomerKeyCookie);
  const setWishlistCustomerKeyStorage =
    _setWishlistCustomerKey ??
    ((customerKey: string) => {
      Cookies.set(WISHLIST_GUEST_USER_COOKIE_KEY, customerKey);
    });

  const [wishlistKey, setWishlistKey] = useState<{
    customerKey?: string;
  }>({
    customerKey: undefined,
  });

  const setCustomerKey = useCallbackRef((customerKey: string) => {
    setWishlistKey({
      customerKey,
    });
    void setWishlistCustomerKeyStorage(customerKey);
  });

  const contextValue = useMemo(
    () => ({
      customerKey: wishlistKey.customerKey,
      customerKeyType: wishlistKey.customerKey ? CustomerKeyType.CUSTOMER : CustomerKeyType.GUEST,
      setCustomerKey,
    }),
    [wishlistKey.customerKey, setCustomerKey],
  );

  useEffect(() => {
    (async () => {
      const { wishlistGuestKey } = await getWishlistCustomerKey();
      if (wishlistGuestKey) {
        setWishlistKey({
          customerKey: wishlistGuestKey,
        });
      }
    })();
  }, [getWishlistCustomerKey]);

  return <WishlistCustomerKeyContextProvider value={contextValue}>{children}</WishlistCustomerKeyContextProvider>;
};

export const useWishlistCustomerKey = useWishlistCustomerKeyContext;
